/**
 * Forge Page Builder — Valley Vitality section schema (@vv/sections).
 *
 * This is the VV DIALECT of the Forge page builder (canonical builder:
 * growwithhype repo, `@hdm/sections`). Same architecture, VV's own section
 * vocabulary: a `Page` is a list of `Section`s; each section has a `type`
 * discriminator + a typed payload. The renderer maps `type` → one Astro
 * component; the admin generates section forms by introspecting these Zod
 * shapes. Pages are stored in D1 `page_blocks` as one `_meta` row plus
 * ordered `section-<n>` rows (content_type 'json').
 *
 * RECORD-BOUND SECTIONS (VV-specific doctrine): several section types carry
 * only header copy — their items auto-pull from VV's D1 collection tables
 * at build time (drip_menu, marketing_services, faqs, program_steps,
 * testimonials). The records are edited as LISTS inside the Pages editor
 * (same takeover, generated forms, autosave, live preview — see "Record
 * lists" below); the section decides where and with what framing they
 * render. This is the same idea as growwithhype's `case-rows` auto-pull,
 * promoted to a first-class rule (Matt, 2026-09-10: record-driven pages are
 * record-bound sections, never bare Astro routes).
 *
 * Consumed by:
 *   - apps/site  — build-time validation of D1/JSON pages + PageRenderer
 *   - apps/admin — the Pages editor introspects SECTION_SCHEMAS
 *
 * IMPORTANT: plain `zod` import, NOT `astro:content` — the admin SPA has
 * no Astro runtime.
 */

import { z } from 'zod';

// ── Shared atom schemas ────────────────────────────────────────────

const Cta = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  primary: z.boolean().default(false),
  /** External link — opens new tab + sets noopener noreferrer. */
  external: z.boolean().default(false),
});
export type Cta = z.infer<typeof Cta>;

/** Headline copy — trusted HTML. VV's signature moves: a `<span
 * class="script">…</span>` line inside display headlines and italic
 * accent spans. Author-controlled JSON only; never user input. */
const Headline = z.string().min(1);

/** Image source — absolute URL (R2 media URL) or root-relative path. */
const ImageSrc = z
  .string()
  .min(1)
  .refine((s) => s.startsWith('/') || /^https?:\/\//.test(s), {
    message: 'Must be an absolute URL or a root-relative path starting with /',
  });

/** Every section schema = its typed payload + shared editor-managed
 * fields. `hidden` keeps the section in the stored list (neighbor ids
 * never shift) while PageRenderer skips it. `anchor` is an optional
 * in-page link target (`/glp1#how-it-works`) that PageRenderer emits as an
 * empty element right before the section. The admin form generator skips
 * every key in SECTION_INTERNAL_FIELDS. */
const sectionObject = <T extends z.ZodRawShape>(shape: T) =>
  z.object({
    ...shape,
    hidden: z.boolean().optional(),
    anchor: z
      .string()
      .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, digits and dashes only')
      .optional(),
  });

// ── Section schemas ────────────────────────────────────────────────

/** Home/landing hero. `portrait` variant is the VV home composition
 * (portrait photo + signature + stat chip + bottom ribbon); `simple` is
 * headline/body/CTAs beside one photo, vertically centered (GLP-1 hero);
 * `profile` is the same split top-aligned with a multi-paragraph bio
 * (About hero). */
const HeroSection = sectionObject({
  type: z.literal('hero'),
  variant: z.enum(['portrait', 'simple', 'profile']).default('portrait'),
  eyebrow: z.string().optional(),
  title: Headline,
  body: z.string().optional(),
  ctas: z.array(Cta).default([]),
  /** Checkmark pills under the CTAs ("RN-administered", …). */
  pills: z.array(z.string()).default([]),
  image: ImageSrc.optional(),
  imageAlt: z.string().optional(),
  /** Portrait variant only — the signature block over the photo. */
  signatureName: z.string().optional(),
  signatureSub: z.string().optional(),
  /** Portrait variant only — the floating stat chip. */
  statValue: z.string().optional(),
  statLabel: z.string().optional(),
  /** Portrait variant only — the two-sided ribbon under the hero. */
  ribbonLeft: z.string().optional(),
  ribbonRight: z.string().optional(),
});
export type HeroSection = z.infer<typeof HeroSection>;

const PageHeaderSection = sectionObject({
  type: z.literal('page-header'),
  eyebrow: z.string().optional(),
  title: Headline,
  body: z.string().optional(),
  ctas: z.array(Cta).default([]),
});

/** Thin reassurance strip under the hero. The city list renders from the
 * `service_area` business setting (Settings → Business) — not stored here. */
const TrustStripSection = sectionObject({
  type: z.literal('trust-strip'),
  /** Left text, e.g. "Now booking — Mat-Su Valley & Anchorage". */
  leftText: z.string().min(1),
  note: z.string().optional(),
});

/** RECORD-BOUND: cards/rows auto-pull from `marketing_services` (active,
 * in display order). Edit the services themselves under Services. */
const ServiceCardsSection = sectionObject({
  type: z.literal('service-cards'),
  variant: z.enum(['cards', 'rows']).default('cards'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  body: z.string().optional(),
});

/** RECORD-BOUND: items auto-pull from `drip_menu` (active, grouped by
 * category; shots + program items stay in the booking widget, not here). */
const DripMenuSection = sectionObject({
  type: z.literal('drip-menu'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  body: z.string().optional(),
  footnote: z.string().optional(),
});

/** RECORD-BOUND: quotes auto-pull from `testimonials` (active, ordered). */
const TestimonialsSection = sectionObject({
  type: z.literal('testimonials'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  /** Small privacy/context note under the header. */
  note: z.string().optional(),
});

/** RECORD-BOUND: questions auto-pull from `faqs` where page = pageKey.
 * Emits FAQPage JSON-LD for GEO. */
const FaqSection = sectionObject({
  type: z.literal('faq'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  /** Which FAQ group renders here (faqs.page value: services, glp1, …). */
  pageKey: z.string().min(1),
  /** Optional "Still have questions?" button under the list. */
  cta: Cta.optional(),
});

/** Numbered steps. Inline items by default; set `fromSteps` to a
 * program_steps page key (e.g. "glp1") to auto-pull records instead.
 * `illustrated` shows the three hand-drawn how-it-works icons (home);
 * `numbered` is the big-italic-number card grid (GLP-1). */
const ProcessSection = sectionObject({
  type: z.literal('process'),
  variant: z.enum(['numbered', 'illustrated']).default('numbered'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  steps: z
    .array(
      z.object({
        title: z.string().min(1),
        body: z.string().min(1),
      }),
    )
    .default([]),
  /** RECORD-BOUND mode — program_steps.page key; overrides inline steps. */
  fromSteps: z.string().optional(),
  cta: Cta.optional(),
});

/** Titled item grid — beliefs, differentiators, approach pillars.
 * `numbered-dark` = the deep-green band with italic 01–04 kickers (home
 * "why"); `plain` = simple headings on the page background (about). */
const PrinciplesSection = sectionObject({
  type: z.literal('principles'),
  variant: z.enum(['plain', 'numbered-dark']).default('plain'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  items: z
    .array(
      z.object({
        title: z.string().min(1),
        body: z.string().min(1),
      }),
    )
    .min(1),
});

const StatsSection = sectionObject({
  type: z.literal('stats'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  stats: z
    .array(
      z.object({
        number: z.string(),
        label: z.string(),
        caption: z.string().optional(),
      }),
    )
    .min(1),
});

/** Photo beside headline + rich body + CTAs. `feature` = warm band,
 * tall 5:6 photo (home owner story); `soft` = mint band, square photo,
 * green-grey body text (About "off the clock"). */
const MediaTextSection = sectionObject({
  type: z.literal('media-text'),
  variant: z.enum(['feature', 'soft']).default('feature'),
  eyebrow: z.string().optional(),
  title: Headline,
  /** Rich body — may hold multiple <p> paragraphs. */
  body: z.string().optional(),
  ctas: z.array(Cta).default([]),
  image: ImageSrc.optional(),
  imageAlt: z.string().optional(),
  imageCaption: z.string().optional(),
  mediaSide: z.enum(['left', 'right']).default('left'),
});

/** Full-bleed cinematic photo with caption + small meta line. Until a
 * photo is uploaded the band shows the styled placeholder (imageAlt is
 * its caption). */
const ImageBandSection = sectionObject({
  type: z.literal('image-band'),
  image: ImageSrc.optional(),
  imageAlt: z.string().min(1),
  caption: z.string().optional(),
  meta: z.string().optional(),
});

/** Label/value rows — the GLP-1 pricing table, quick-facts blocks. */
const FactsTableSection = sectionObject({
  type: z.literal('facts-table'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  body: z.string().optional(),
  rows: z
    .array(
      z.object({
        label: z.string().min(1),
        value: z.string().min(1),
      }),
    )
    .min(1),
  note: z.string().optional(),
});

/** Checkmark list — "what's included" blocks. */
const ChecklistSection = sectionObject({
  type: z.literal('checklist'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  items: z.array(z.string().min(1)).min(1),
});

/** Free-form rich text (trusted HTML) with optional header. */
const ProseSection = sectionObject({
  type: z.literal('prose'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  html: z.string().min(1),
});

/** Small emphasized note card (the GLP-1 "what this program is" note). */
const CalloutSection = sectionObject({
  type: z.literal('callout'),
  eyebrow: z.string().optional(),
  body: z.string().min(1),
});

/** One large dramatic quote + attribution. */
const QuoteSection = sectionObject({
  type: z.literal('quote'),
  quote: z.string().min(1),
  attribution: z.string().optional(),
  context: z.string().optional(),
});

/** Photo grid. */
const GallerySection = sectionObject({
  type: z.literal('gallery'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  columns: z.number().int().min(2).max(4).default(3),
  images: z
    .array(
      z.object({
        src: ImageSrc,
        alt: z.string(),
        /** Span two columns for a wide feature shot. */
        wide: z.boolean().default(false),
      }),
    )
    .min(1),
});

/** Dark primary CTA band with the script accent line + mountain wash —
 * VV's footer CTA. Usually lives as the shared `footer-cta` global block. */
const CtaPanelSection = sectionObject({
  type: z.literal('cta-panel'),
  /** Script-font lead-in line ("Wherever you are"). */
  script: z.string().optional(),
  title: Headline,
  body: z.string().optional(),
  ctas: z.array(Cta).default([]),
});

/** Service-area block — routes, hours, and the city pills. Cities render
 * from the `service_area` business setting; routes/hours live here. */
const ServiceAreaSection = sectionObject({
  type: z.literal('service-area'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  body: z.string().optional(),
  routes: z
    .array(
      z.object({
        label: z.string().min(1),
        days: z.string().min(1),
      }),
    )
    .default([]),
  hoursLabel: z.string().optional(),
  hoursValue: z.string().optional(),
});

/** The contact panel — message form beside the sidebar (direct lines
 * from business settings, hours rows, booking card). One section because
 * it's one two-column layout on the page. Form posts to /api/contact
 * with a mailto fallback. */
const ContactPanelSection = sectionObject({
  type: z.literal('contact-panel'),
  formTitle: z.string().min(1),
  formNote: z.string().optional(),
  hours: z
    .array(
      z.object({
        days: z.string().min(1),
        value: z.string().min(1),
        muted: z.boolean().default(false),
      }),
    )
    .default([]),
  bookingEyebrow: z.string().optional(),
  bookingTitle: z.string().optional(),
  bookingCtaLabel: z.string().optional(),
});

/** The GLP-1 pricing band — label/value rows beside the what's-included
 * checklist, on the deep-green background. One section because it's one
 * two-column layout. */
const ProgramPricingSection = sectionObject({
  type: z.literal('program-pricing'),
  eyebrow: z.string().optional(),
  title: Headline,
  body: z.string().optional(),
  rows: z
    .array(
      z.object({
        label: z.string().min(1),
        value: z.string().min(1),
      }),
    )
    .min(1),
  includedEyebrow: z.string().optional(),
  includedTitle: Headline.optional(),
  includedItems: z.array(z.string().min(1)).default([]),
});

/** The booking chooser + live phone-consult scheduler (React island).
 * Two doors: book the free consultation here, or go straight to the
 * 3rd-party signup. The direct-signup card renders only when the
 * `booking_url` business setting is set (Settings → Booking) — its link
 * always comes from that setting, never from this payload, so the URL
 * stays a single site-wide switch. Consult length/availability come from
 * Settings → Booking. */
const BookingWidgetSection = sectionObject({
  type: z.literal('booking-widget'),
  eyebrow: z.string().optional(),
  title: Headline.optional(),
  body: z.string().optional(),
  /** Chooser card 1 — the free consultation. */
  consultTitle: z.string().optional(),
  consultBody: z.string().optional(),
  /** Chooser card 2 — direct 3rd-party signup (hidden until booking_url is set). */
  directTitle: z.string().optional(),
  directBody: z.string().optional(),
  directCtaLabel: z.string().optional(),
});

/** Escape hatch — trusted raw markup. Dev-only in practice. */
const RawHtmlSection = sectionObject({
  type: z.literal('raw'),
  html: z.string().min(1),
});

/** Reference to a GLOBAL BLOCK — a shared section stored once
 * (page_blocks page '_global') and reused across pages. Edit the block
 * in the admin's Blocks page and every page using it updates on the next
 * Publish. The site resolves refs at build time; an unresolvable ref
 * renders nothing rather than failing the build. One level only — a
 * global block may not itself contain a global ref. */
const GlobalRefSection = sectionObject({
  type: z.literal('global'),
  blockId: z.string().min(1),
});

// ── Section union + Page schema ────────────────────────────────────

export const SectionSchema = z.discriminatedUnion('type', [
  HeroSection,
  PageHeaderSection,
  TrustStripSection,
  ServiceCardsSection,
  DripMenuSection,
  TestimonialsSection,
  FaqSection,
  ProcessSection,
  PrinciplesSection,
  StatsSection,
  MediaTextSection,
  ImageBandSection,
  FactsTableSection,
  ChecklistSection,
  ProseSection,
  CalloutSection,
  QuoteSection,
  GallerySection,
  CtaPanelSection,
  ServiceAreaSection,
  ContactPanelSection,
  ProgramPricingSection,
  BookingWidgetSection,
  RawHtmlSection,
  GlobalRefSection,
]);

export type Section = z.infer<typeof SectionSchema>;
export type SectionType = Section['type'];

/** Page metadata + section list. Used by build-time D1 validation and
 * the admin Pages editor. */
export const PageSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  ogImage: z.string().optional(),
  noindex: z.boolean().default(false),
  /** SEO optimizer target phrase. Admin-only; the site ignores it. */
  focusKeyword: z.string().optional(),
  /** Page-specific JSON-LD payload (Service / FAQPage / Breadcrumb…). */
  jsonLd: z.array(z.unknown()).default([]),
  sections: z.array(SectionSchema).min(1),
});

export type Page = z.infer<typeof PageSchema>;

// ── Editor metadata ────────────────────────────────────────────────
// Everything below exists for apps/admin: the section palette, the
// per-type Zod shapes the form generator introspects, and valid default
// payloads for freshly-added sections.

export const SECTION_SCHEMAS: Record<SectionType, z.ZodObject<z.ZodRawShape>> = {
  hero: HeroSection,
  'page-header': PageHeaderSection,
  'trust-strip': TrustStripSection,
  'service-cards': ServiceCardsSection,
  'drip-menu': DripMenuSection,
  testimonials: TestimonialsSection,
  faq: FaqSection,
  process: ProcessSection,
  principles: PrinciplesSection,
  stats: StatsSection,
  'media-text': MediaTextSection,
  'image-band': ImageBandSection,
  'facts-table': FactsTableSection,
  checklist: ChecklistSection,
  prose: ProseSection,
  callout: CalloutSection,
  quote: QuoteSection,
  gallery: GallerySection,
  'cta-panel': CtaPanelSection,
  'service-area': ServiceAreaSection,
  'contact-panel': ContactPanelSection,
  'program-pricing': ProgramPricingSection,
  'booking-widget': BookingWidgetSection,
  raw: RawHtmlSection,
  global: GlobalRefSection,
};

/** Shape keys that are editor plumbing, not content fields. `anchor` is
 * link plumbing — other pages' links depend on it, so it stays out of the
 * client-facing forms. */
export const SECTION_INTERNAL_FIELDS: ReadonlySet<string> = new Set(['type', 'hidden', 'anchor']);

/** Human labels + hints for the editor's "Add section" palette. The
 * record-bound hints tell the operator where the items are edited. */
export const SECTION_META: Record<SectionType, { label: string; hint: string }> = {
  hero: { label: 'Hero', hint: 'Page opener — headline, CTAs, pills, portrait or photo' },
  'page-header': { label: 'Page header', hint: 'Inner-page opener — eyebrow, title, intro, CTA' },
  'trust-strip': { label: 'Trust strip', hint: 'Thin reassurance line — cities come from Settings → Business' },
  'service-cards': { label: 'Services', hint: 'Service cards or detail rows — items come from the Services list' },
  'drip-menu': { label: 'Drip menu', hint: 'IV drip cards — items come from the Drip menu list' },
  testimonials: { label: 'Testimonials', hint: 'Quote cards — items come from the Testimonials list' },
  faq: { label: 'FAQ', hint: 'Accordion + FAQPage structured data — questions come from the FAQs list' },
  process: { label: 'Steps', hint: 'Numbered how-it-works steps (inline, or pulled from Program steps)' },
  principles: { label: 'Card grid', hint: 'Titled cards — differentiators, approach pillars' },
  stats: { label: 'Stats', hint: 'Big-number stat row' },
  'media-text': { label: 'Photo + text', hint: 'Photo beside headline, paragraphs, CTAs' },
  'image-band': { label: 'Photo band', hint: 'Full-bleed photo with caption' },
  'facts-table': { label: 'Facts table', hint: 'Label/value rows — pricing, quick facts' },
  checklist: { label: 'Checklist', hint: 'Checkmark list — what’s included' },
  prose: { label: 'Text block', hint: 'Free-form rich text' },
  callout: { label: 'Callout', hint: 'Small emphasized note card' },
  quote: { label: 'Pull quote', hint: 'One large quote + attribution' },
  gallery: { label: 'Photo gallery', hint: 'Photo grid — 2/3/4 columns' },
  'cta-panel': { label: 'CTA band', hint: 'Dark call-to-action band with the script accent' },
  'service-area': { label: 'Service area', hint: 'Routes + hours — city pills come from Settings → Business' },
  'contact-panel': { label: 'Contact panel', hint: 'Message form + direct lines (from Settings), hours, booking card' },
  'program-pricing': { label: 'Program pricing', hint: 'Dark band — price rows beside the what’s-included list' },
  'booking-widget': { label: 'Booking', hint: 'Free-consult scheduler + direct-signup door (link set in Settings → Booking)' },
  raw: { label: 'Raw HTML', hint: 'Escape hatch — trusted raw markup' },
  global: { label: 'Shared block', hint: 'Reusable section — edit once in Blocks, updates everywhere' },
};

/** Valid default payloads for the "Add section" action. Every entry must
 * round-trip through SectionSchema.parse — enforced by the package test. */
export const SECTION_DEFAULTS: Record<SectionType, Record<string, unknown>> = {
  hero: { type: 'hero', variant: 'simple', title: 'New hero headline', ctas: [{ label: 'Book a Free Consultation', href: '/book' }] },
  'page-header': { type: 'page-header', title: 'Page title' },
  'trust-strip': { type: 'trust-strip', leftText: 'Now booking — Mat-Su Valley & Anchorage' },
  'service-cards': { type: 'service-cards', variant: 'cards', title: 'Care that comes to you' },
  'drip-menu': { type: 'drip-menu', title: 'The drip menu' },
  testimonials: { type: 'testimonials', title: 'What clients say' },
  faq: { type: 'faq', pageKey: 'services', title: 'Common questions' },
  process: { type: 'process', title: 'How it works', steps: [{ title: 'New step', body: 'What happens in this step.' }] },
  principles: { type: 'principles', items: [{ title: 'New card', body: 'Card body copy.' }] },
  stats: { type: 'stats', stats: [{ number: '14', label: 'New stat' }] },
  'media-text': { type: 'media-text', title: 'New section headline', body: '<p>Body copy.</p>' },
  'image-band': { type: 'image-band', imageAlt: 'Wide Alaska landscape photo' },
  'facts-table': { type: 'facts-table', rows: [{ label: 'New row', value: 'Value' }] },
  checklist: { type: 'checklist', items: ['New item'] },
  prose: { type: 'prose', html: '<p>New text block.</p>' },
  callout: { type: 'callout', body: 'A short emphasized note.' },
  quote: { type: 'quote', quote: 'A short pull quote.' },
  gallery: { type: 'gallery', images: [{ src: '/logo-landscape.png', alt: 'Photo' }] },
  'cta-panel': { type: 'cta-panel', script: 'Wherever you are', title: 'real care should meet you there.', ctas: [{ label: 'Book my consultation', href: '/book' }] },
  'service-area': { type: 'service-area', title: 'Where we go' },
  'contact-panel': {
    type: 'contact-panel',
    formTitle: 'Send a message',
    hours: [{ days: 'Tues – Fri', value: 'noon – 4 pm' }],
  },
  'program-pricing': {
    type: 'program-pricing',
    title: 'Pricing, all in.',
    rows: [{ label: 'Initial consult', value: 'Free' }],
    includedItems: ['New included item'],
  },
  'booking-widget': {
    type: 'booking-widget',
    title: 'Book your free phone consultation.',
    consultTitle: 'Book a free 20-minute consultation',
    consultBody: 'Pick a time and Channa calls you. No card, no commitment.',
    directTitle: 'Ready to start? Sign up directly',
    directBody: 'Skip the consult and sign up through our booking site.',
    directCtaLabel: 'Continue to signup',
  },
  raw: { type: 'raw', html: '<!-- raw html -->' },
  global: { type: 'global', blockId: 'unset' },
};

/** Ordered palette list for the "Add section" menu. 'global' is added
 * through the Blocks picker instead, so it's excluded here. */
export const SECTION_TYPES: SectionType[] = [
  'hero',
  'page-header',
  'trust-strip',
  'service-cards',
  'drip-menu',
  'testimonials',
  'faq',
  'process',
  'principles',
  'stats',
  'media-text',
  'image-band',
  'facts-table',
  'checklist',
  'prose',
  'callout',
  'quote',
  'gallery',
  'cta-panel',
  'service-area',
  'contact-panel',
  'program-pricing',
  'booking-widget',
  'raw',
];

// ── Global blocks ──────────────────────────────────────────────────

/** page_blocks.page value that holds the shared-block library. */
export const GLOBAL_PAGE_KEY = '_global';

export const GlobalBlockSchema = z.object({
  /** Human name shown in the Blocks page + editor picker. */
  name: z.string().min(1),
  section: SectionSchema,
});
export type GlobalBlock = z.infer<typeof GlobalBlockSchema>;

// ── Record lists ───────────────────────────────────────────────────
// The records RECORD-BOUND sections auto-pull, edited as Lists in the
// Pages editor (picker → Lists). These shapes are the editor's
// validation gate AND its generated forms, so they hold the EDITABLE
// fields only (ids, slugs, display_order, timestamps are plumbing) in
// form order. They MUST stay in step with the API's PATCH payloads
// (apps/api/src/routes/<list>.ts): a draft that passes here must never
// be refused there. Nullable columns are `.nullable().optional()` — the
// form clears them to absent, the editor sends null.

export const DRIP_CATEGORIES = [
  'hydration',
  'performance',
  'beauty',
  'wellness',
  'shots',
  'program',
] as const;

/** Drip categories the Services page drip-menu section shows. Shots and
 * the GLP-1 program appear in the booking widget only. */
export const DRIP_MENU_PAGE_CATEGORIES = ['hydration', 'performance', 'beauty', 'wellness'] as const;

export const SERVICE_ICONS = [
  'drop',
  'syringe',
  'spark',
  'beaker',
  'leaf',
  'scale',
  'pin',
  'clock',
  'check',
  'arrow',
] as const;

const OptText = z.string().nullable().optional();

const DripRecord = z.object({
  name: z.string().min(1),
  category: z.enum(DRIP_CATEGORIES),
  price_cents: z.number().int().nonnegative(),
  duration_minutes: z.number().int().positive().nullable().optional(),
  description: OptText,
  tag: OptText,
  is_popular: z.boolean(),
  image_url: OptText,
  is_active: z.boolean(),
});

const ServiceRecord = z.object({
  title: z.string().min(1),
  subtitle: OptText,
  short_title: z.string().min(1),
  card_blurb: OptText,
  icon: z.enum(SERVICE_ICONS),
  image_url: OptText,
  best_for: z.array(z.string()),
  includes: z.array(z.string()),
  price_label: z.string().min(1),
  price_unit: z.string().min(1),
  tag: OptText,
  is_program: z.boolean(),
  link_to: OptText,
  is_active: z.boolean(),
});

const FaqRecord = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
  /** FAQ group — an FAQ section's pageKey picks one group. */
  page: z.string().min(1),
  is_active: z.boolean(),
});

const StepRecord = z.object({
  step_number: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  /** Step group — a Steps section's fromSteps picks one group. */
  page: z.string().min(1),
  is_active: z.boolean(),
});

const TestimonialRecord = z.object({
  quote: z.string().min(1),
  attribution: z.string().min(1),
  location: OptText,
  context: OptText,
  is_active: z.boolean(),
});

export type ListKey = 'drips' | 'services' | 'faqs' | 'steps' | 'testimonials';

/** Picker order. */
export const LIST_KEYS: ListKey[] = ['drips', 'services', 'faqs', 'steps', 'testimonials'];

export const RECORD_SCHEMAS: Record<ListKey, z.ZodObject<z.ZodRawShape>> = {
  drips: DripRecord,
  services: ServiceRecord,
  faqs: FaqRecord,
  steps: StepRecord,
  testimonials: TestimonialRecord,
};

/** Record keys the generated forms never render — the rail's eye toggle
 * and the panel's "Shown on site" switch own `is_active`; a group key is
 * picked from the group select. */
export const RECORD_INTERNAL_FIELDS: ReadonlySet<string> = new Set(['is_active']);

export interface ListMeta {
  /** Picker + rail heading ("Drip menu"). */
  label: string;
  /** One record, lower-case ("drip") — "+ Add drip". */
  itemLabel: string;
  hint: string;
  /** The record-bound section type that renders this list. */
  sectionType: SectionType;
  /** Records split into groups by this key; one section shows one group. */
  groupBy?: 'page';
}

export const LIST_META: Record<ListKey, ListMeta> = {
  drips: {
    label: 'Drip menu',
    itemLabel: 'drip',
    hint: 'IV drips, shots and the GLP-1 program. The Services page shows the IV categories; shots and the program are booking-only.',
    sectionType: 'drip-menu',
  },
  services: {
    label: 'Services',
    itemLabel: 'service',
    hint: 'Home-page service cards and the Services page detail rows.',
    sectionType: 'service-cards',
  },
  faqs: {
    label: 'FAQs',
    itemLabel: 'question',
    hint: 'Grouped by page — each FAQ section shows one group.',
    sectionType: 'faq',
    groupBy: 'page',
  },
  steps: {
    label: 'Program steps',
    itemLabel: 'step',
    hint: 'Grouped by page — a Steps section set to pull steps shows one group.',
    sectionType: 'process',
    groupBy: 'page',
  },
  testimonials: {
    label: 'Testimonials',
    itemLabel: 'testimonial',
    hint: 'Client quote cards.',
    sectionType: 'testimonials',
  },
};

/** Which list (and group) a section renders records from — null for a
 * section that isn't record-bound (or a Steps section with inline steps). */
export function listForSection(
  section: Record<string, unknown>,
): { list: ListKey; group?: string } | null {
  switch (section.type) {
    case 'drip-menu':
      return { list: 'drips' };
    case 'service-cards':
      return { list: 'services' };
    case 'testimonials':
      return { list: 'testimonials' };
    case 'faq':
      return typeof section.pageKey === 'string' && section.pageKey
        ? { list: 'faqs', group: section.pageKey }
        : null;
    case 'process':
      return typeof section.fromSteps === 'string' && section.fromSteps
        ? { list: 'steps', group: section.fromSteps }
        : null;
    default:
      return null;
  }
}

// ── Page templates ─────────────────────────────────────────────────

export interface PageTemplate {
  key: string;
  label: string;
  hint: string;
  sections: Array<{ type: SectionType; overrides?: Record<string, unknown> }>;
}

/** Starter layouts for "New page". Payloads come from SECTION_DEFAULTS,
 * so every template is valid by construction (enforced by the test). */
export const PAGE_TEMPLATES: PageTemplate[] = [
  {
    key: 'blank',
    label: 'Blank',
    hint: 'Page header + one text block',
    sections: [{ type: 'page-header' }, { type: 'prose' }],
  },
  {
    key: 'inner',
    label: 'Standard page',
    hint: 'Header, photo + text, FAQ, footer CTA',
    sections: [
      { type: 'page-header' },
      { type: 'media-text' },
      { type: 'faq' },
      { type: 'global', overrides: { blockId: 'footer-cta' } },
    ],
  },
  {
    key: 'landing',
    label: 'Landing page',
    hint: 'Hero, checklist, facts, CTA band',
    sections: [
      { type: 'hero', overrides: { variant: 'simple' } },
      { type: 'checklist' },
      { type: 'facts-table' },
      { type: 'cta-panel' },
    ],
  },
];

// ── Nav + Footer config (site chrome) ──────────────────────────────
// Stored in D1 settings as JSON under NAV_SETTINGS_KEY /
// FOOTER_SETTINGS_KEY, edited on the admin's Navigation page. Site
// chrome is config, not page content — same doctrine as the canonical
// builder. Defaults mirror the site's launch chrome so a missing
// setting renders the site exactly as shipped.

export const NavConfigSchema = z.object({
  links: z.array(z.object({ label: z.string().min(1), href: z.string().min(1) })),
  cta: Cta,
  /** Show the call button (business phone) in the mobile drawer. */
  drawerCall: z.boolean().default(true),
});
export type NavConfig = z.infer<typeof NavConfigSchema>;

export const NAV_SETTINGS_KEY = 'nav_config';

export const DEFAULT_NAV: NavConfig = {
  links: [
    { label: 'Services', href: '/services' },
    { label: 'GLP-1 Program', href: '/glp1' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ],
  cta: { label: 'Book a Free Consultation', href: '/book', primary: true, external: false },
  drawerCall: true,
};

export const FooterConfigSchema = z.object({
  tagline: z.string().min(1),
  columns: z.array(
    z.object({
      heading: z.string().min(1),
      links: z.array(z.object({ label: z.string().min(1), href: z.string().min(1) })),
    }),
  ),
  /** Line under the phone/email in the contact column (hours note). */
  contactNote: z.string().optional(),
  cta: Cta,
});
export type FooterConfig = z.infer<typeof FooterConfigSchema>;

export const FOOTER_SETTINGS_KEY = 'footer_config';

export const DEFAULT_FOOTER: FooterConfig = {
  tagline:
    'Concierge mobile wellness for the Mat-Su Valley and Anchorage. Real care, delivered to your door.',
  columns: [
    {
      heading: 'Services',
      links: [
        { label: 'IV Hydration', href: '/services#hydration' },
        { label: 'Vitamin Injections', href: '/services#injections' },
        { label: 'Peptide Therapy', href: '/services#peptides' },
        { label: 'NAD+ Therapy', href: '/services#nad' },
        { label: 'GLP-1 Weight Loss', href: '/glp1' },
      ],
    },
    {
      heading: 'Visit',
      links: [
        { label: 'About Channa', href: '/about' },
        { label: 'Service Area', href: '/contact#area' },
        { label: 'Contact', href: '/contact' },
        { label: 'Book a Consult', href: '/book' },
      ],
    },
  ],
  contactNote: 'Tues–Fri · noon–4pm',
  cta: { label: 'Book a Free Consultation', href: '/book', primary: true, external: false },
};
